// ledger-scale.ts — THE LEDGER'S OWN PRECISION, once for the whole console (SW-FORMSB, Astra round 2f, 23 Oct 2026).
//
// bff/src/lib/assets.ts ASSET_SCALE is the table the books post at: a currency to the fil, a stablecoin to six places, XLM seven, SOL and the
// Ledger device's GRAM nine, the other coins eight. The console kept three hand copies of a piece of it, each wrong somewhere else: the deal
// page left out GRAM, XLM and TRX (a figure it called square was rounded at the wrong place), Convert balance had SOL at eight, and the desk
// swap form held every coin at eight. A balance "closes to zero" only at the precision the books keep, and a figure is printed at that
// precision or it lies by a ninth decimal. This is the ONE copy.
//
// It is the BFF table, code for code and digit for digit: tests/forms-move-convert-cheque parses bff/src/lib/assets.ts and fails when the two
// differ, and fails when a screen keeps a table of its own. A new asset is added THERE first (scripts/check-asset-scales.mjs holds the BFF to
// admin.asset_registry), then here — the test says so by name.
//
// And the ONE rounding rule: the BFF's (bff/src/lib/money.ts toMinor — half-even on the decimal text). Every figure the console brings to the
// books' place, for a box that will be posted or for a closure claim, goes through ledgerText below; the BFF is imported into the test and
// the two are held together, tie by tie.

import { foldAsset } from './naming';

export const LEDGER_SCALE: Readonly<Record<string, number>> = {
  AED: 2,
  INR: 2,
  USD: 2,
  EUR: 2,
  USDTTRC: 6,
  USDTERC: 6,
  USDTBEP: 6,
  USDTSOL: 6,
  USDCTRC: 6,
  USDCERC: 6,
  USDCBEP: 6,
  USDCBASE: 6,
  BTC: 8,
  ETH: 8,
  SOL: 9,
  USDT: 6,
  USDC: 6,
  XLM: 7,
  BNB: 8,
  TRX: 6,
  GRAM: 9,
  APT: 8,
};

/** The finest place any asset in the table is kept to. A code the table does not hold gets THIS, never a coarser guess: the books refuse
 *  an unknown code (bff assetScale throws), so the console can only draw it — and rounding it short would call a balance square that the
 *  books, at their finer place, still hold open. */
export const FINEST_SCALE = Math.max(...Object.values(LEDGER_SCALE));

/** How many decimal places the books keep this asset to. Exact code first (USDTTRC, USDCBASE), then the folded one (a legacy USDTTRC20 is
 *  USDT), then the finest place. */
export function ledgerScale(asset: string | null | undefined): number {
  const code = (asset ?? '').toUpperCase();
  return LEDGER_SCALE[code] ?? LEDGER_SCALE[foldAsset(code)] ?? FINEST_SCALE;
}

/** A decimal text brought to `places`, HALF-EVEN on the magnitude — a tie goes to the EVEN digit, 0.0000005 to 0.000000 and 0.0000015 to
 *  0.000002 — exactly what bff/src/lib/money.ts toMinor does (and fromMinor writes back). Digits in, digits out: no binary number is made. */
function roundHalfEven(text: string, places: number): string {
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) return text;          // not a plain decimal: handed back as it came
  const frac = m[3] ?? '';
  const rest = frac.slice(places);
  let magnitude = BigInt((m[2] || '0') + frac.slice(0, places).padEnd(places, '0'));
  if (rest !== '') {
    const first = rest.charCodeAt(0) - 48;                                // the leading digit that is dropped
    if (first > 5 || (first === 5 && (/[1-9]/.test(rest.slice(1)) || magnitude % 2n === 1n))) magnitude += 1n;
  }
  const digits = magnitude.toString().padStart(places + 1, '0');
  const cut = digits.length - places;
  return `${m[1] === '-' && magnitude !== 0n ? '-' : ''}${digits.slice(0, cut)}${places > 0 ? `.${digits.slice(cut)}` : ''}`;
}

/** What String() writes for a number, with an exponent written out: 1e-7 is 0.0000001. The shortest decimal that gives the number back is
 *  the figure a venue or a person wrote, and the one the BFF's toMinor(String(amount)) is handed. */
function plainDecimal(n: number): string {
  return expandExponent(String(n));
}

/** A decimal text with its exponent written out ("1.25e-10" is 0.000000000125, "1.25e+20" is 125000000000000000000); text without one is handed back as it came. */
function expandExponent(s: string): string {
  const m = /^(-?)(\d+)(?:\.(\d+))?e([+-]?\d+)$/i.exec(s);
  if (!m) return s;
  const digits = m[2]! + (m[3] ?? ''), point = m[2]!.length + Number(m[4]);   // the decimal point sits `point` digits in
  const body = point <= 0 ? `0.${'0'.repeat(-point)}${digits}`
    : point >= digits.length ? digits + '0'.repeat(point - digits.length)
    : `${digits.slice(0, point)}.${digits.slice(point)}`;
  return m[1] + body;
}

/** A figure AS THE BOOKS WILL HOLD IT, written as plain text: the decimal a number is (String()), rounded half-even at the asset's place —
 *  the rule the BFF's one choke point applies to every posted amount (lib/numscript.ts: toMinor(String(amount), asset)) — with the trailing
 *  zeros dropped. For a box that is filled for the books (a venue's fills, a balance to convert): the figure in the box is the figure that
 *  posts, rounded the way the books would round it, not another way.
 *
 *  Decimal text arithmetic, never toFixed: toFixed rounds the BINARY value, so a true tie like 3.0000005 TRX goes up or down by luck of how
 *  the number is stored (3.000001 where the books' rule, the even digit, books 3.000000), and 0.125 AED reads 0.13 where the books say 0.12.
 *  tests/forms-move-convert-cheque holds this to the BFF's own toMinor/fromMinor, tie by tie, at every scale. */
export function ledgerText(value: number | string, asset: string | null | undefined): string {
  const text = typeof value === 'number' ? (Number.isFinite(value) ? plainDecimal(value) : String(value)) : expandExponent(value.trim());
  const rounded = roundHalfEven(text, ledgerScale(asset));
  // Trailing zeros go only from a plain decimal — never from anything handed back as it came ("1e5x"), whose zeros are not fractional.
  return /^-?\d+\.\d+$/.test(rounded) ? rounded.replace(/0+$/, '').replace(/\.$/, '') : rounded;
}

/** A figure brought back to its asset's ledger precision, as a number — what the books would hold once floating-point arithmetic has had
 *  its say. The same rule as ledgerText (half-even, the BFF's), so +x and −x always agree about whether a box is square; a result of zero
 *  is +0, never −0. */
export function atLedger(value: number, asset: string | null | undefined): number {
  return Number(ledgerText(value, asset));
}
